const currency = (value) => (value == null || !Number.isFinite(Number(value))
    ? '—'
    : Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }));

const dateLabel = (month) => {
    const match = /^(\d{4})-(\d{2})/.exec(String(month || ''));
    return match ? `${match[2]}/01/${match[1]}` : null;
};

const accountLabel = (reason) => [reason.account_number, reason.account_name || reason.label]
    .filter(Boolean)
    .join(' ');

const humanize = (value) => String(value || 'unknown reason')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const describeImportReason = (reason = {}, { budgetName = 'this budget' } = {}) => {
    const account = accountLabel(reason);
    const when = dateLabel(reason.month);
    const at = when ? ` on ${when}` : '';
    const prefix = account ? `${account}: ` : '';

    switch (reason.code) {
    case 'missing_in_sheet':
        return `${prefix}in ${budgetName} but not in this tab.`;
    case 'metric_check_failed': {
        const metric = String(reason.metric || 'Metric').replace(/\b\w/g, (character) => character.toUpperCase());
        return `${when ? `${when} · ` : ''}${metric}: the tab's subtotal is ${currency(reason.actual)} but its rows add up to ${currency(reason.expected)}.`;
    }
    case 'calculated_in_budget':
        return `${prefix}is calculated in ${budgetName}, but this tab has values for it.`;
    case 'unmapped_sheet_account':
        return `${reason.row ? `Row ${reason.row} · ` : ''}${account || reason.label || 'Sheet row'}: has amounts in the tab but no line in ${budgetName}.`;
    case 'ambiguous_account':
        return `${prefix}matches multiple sheet rows, so Eadin cannot tell which amount to use${at}.`;
    case 'duplicate_budget_account':
        return `${prefix}appears more than once in ${budgetName}.`;
    case 'sign_mismatch':
        return `${prefix}has the opposite sign in the sheet and ${budgetName}.`;
    case 'formula_error':
        return `${prefix || (reason.row ? `Row ${reason.row}: ` : '')}contains a spreadsheet formula error${at}.`;
    case 'invalid_cell':
        return `${prefix || (reason.row ? `Row ${reason.row}: ` : '')}has a cell that is not a valid number${at}.`;
    case 'month_header_invalid':
        return 'The tab must have the twelve budget months in order for the selected year.';
    default:
        return `${prefix}${humanize(reason.code)}.`;
    }
};

const summarizeAppliedImport = (preview = {}, applyResult = {}) => {
    const appliedMonths = Array.isArray(applyResult.months_applied) ? applyResult.months_applied : [];
    const frozenMonths = Array.isArray(applyResult.months_frozen) ? applyResult.months_frozen : [];
    const appliedMonthSet = new Set(appliedMonths.map(String));
    const openChangedCount = (preview.accounts || []).reduce((total, account) => (
        total + (account.values || []).filter((cell) => (
            cell.status === 'update' && appliedMonthSet.has(String(cell.month))
        )).length
    ), 0);
    const amendmentCells = applyResult.readback?.closed_forecast_amendment?.cells;
    const closedForecastChangedCount = Array.isArray(amendmentCells) ? amendmentCells.length : 0;

    return {
        receiptId: applyResult.receipt_id ?? null,
        readbackStatus: applyResult.readback?.status ?? null,
        appliedMonths,
        frozenMonths,
        cellsWritten: Array.isArray(applyResult.cells_applied) ? applyResult.cells_applied.length : null,
        changedCount: openChangedCount + closedForecastChangedCount,
        closedForecastChangedCount,
        demandPlanGross: Boolean(preview.demand_plan_gross_note),
    };
};

const summarizeClosedForecastAmendment = (applyResult = {}) => {
    const amendment = applyResult.readback?.closed_forecast_amendment;
    if (!amendment) return null;
    const months = Array.isArray(amendment.months) ? amendment.months : [];
    const deltas = amendment.net_income_delta_by_month || {};
    const netIncomeDelta = Object.values(deltas).reduce((sum, value) => sum + Number(value || 0), 0);
    const monthSet = new Set(months.map(String));
    const netIncomeAfter = (applyResult.readback?.metrics || [])
        .filter((metric) => metric.metric === 'net income' && monthSet.has(String(metric.month)))
        .reduce((sum, metric) => sum + Number(metric.actual || 0), 0);
    const hasNetIncomeAfter = (applyResult.readback?.metrics || [])
        .some((metric) => metric.metric === 'net income' && monthSet.has(String(metric.month)));
    return {
        months,
        netIncomeBefore: hasNetIncomeAfter ? netIncomeAfter - netIncomeDelta : null,
        netIncomeAfter: hasNetIncomeAfter ? netIncomeAfter : null,
    };
};

module.exports = {
    describeImportReason,
    summarizeAppliedImport,
    summarizeClosedForecastAmendment,
};
